// Ylläpidon "Lisää reitti GPX:stä" -arkki (Passi → Ylläpito → Sisältö).
//
// Kulku: 1) valitaan GPX-tiedosto, 2) palvelin muodostaa linjan ja näyttää esikatselun (pituus, pisteet
// vaativuuksittain, kuva), 3) ylläpitäjä täyttää tiedot ja tallentaa. Mitään ei tallenneta ennen tallennuspainiketta.
//
// Kaksi tapaa:
//   "new"     – uusi reitti: nimi, kunta, vaativuus ja pisteet; palvelin kirjoittaa sen lisäpakettiin.
//   "replace" – vain olemassa olevan reitin linja vaihtuu (esim. linjaton reitti saa jäljen); muut tiedot jäävät.
// Lomakkeen logiikka (tarkistukset, lähetettävä runko, viestit) on lib/routeAdmin.js:ssä, jotta se on testattu.

import { useState } from "preact/hooks";

import { toBase64 } from "../lib/base64.js";
import {
  DIFFICULTY_OPTIONS,
  difficultyLabel,
  dropWarning,
  formErrors,
  municipalities,
  pointsFor,
  replaceCandidates,
  savePayload,
  savedMessage,
  sketchPath,
} from "../lib/routeAdmin.js";
import { Sheet } from "./Sheet.jsx";

const ACCEPT = [".gpx", "application/gpx+xml", "application/xml", "text/xml", "application/octet-stream"].join(",");
const EMPTY = { name: "", municipality: "", area: "", description: "", note: "", difficulty: "none", points: "", length: "", replaceId: "" };
const SKETCH_W = 280;
const SKETCH_H = 140;

export function RouteImportSheet({ app, onClose, onSaved }) {
  const [file, setFile] = useState(null); // { name, b64 }
  const [preview, setPreview] = useState(null); // palvelimen esikatselu
  const [mode, setMode] = useState("new");
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((cur) => ({ ...cur, ...patch }));
  const text = (key) => ({ value: form[key], onInput: (e) => set({ [key]: e.currentTarget.value }) });

  /** Lukee tiedoston ja hakee esikatselun. Nimi ehdotetaan GPX:stä, jos kenttä on vielä tyhjä. */
  const choose = async (f) => {
    if (!f) return;
    setErrors([]);
    setPreview(null);
    setBusy(true);
    try {
      const b64 = toBase64(await f.arrayBuffer());
      const result = await app.api.adminRoutePreview(f.name, b64);
      setFile({ name: f.name, b64 });
      setPreview(result);
      setForm((cur) => ({ ...cur, name: cur.name || result.suggested_name || "" }));
    } catch (e) {
      setFile(null);
      setErrors([e.message]);
    }
    setBusy(false);
  };

  const save = async () => {
    const found = formErrors(form, mode, preview);
    setErrors(found);
    if (found.length) return;
    setBusy(true);
    try {
      const result = await app.api.adminRouteSave(savePayload(file, form, mode));
      app.toast({ kind: "info", ...savedMessage(result) });
      app.refresh(); // uusi reitti ja linja näkyviin omaan listaan ja kartalle
      onSaved?.(result);
      onClose();
    } catch (e) {
      setErrors([e.message]);
      setBusy(false);
    }
  };

  const warning = dropWarning(preview);
  const shownPoints = pointsFor(preview, form.difficulty, form.points);
  const candidates = replaceCandidates(app.content);
  const towns = municipalities(app.content);

  return (
    <Sheet title="Lisää reitti GPX:stä" onClose={onClose}>
      <p class="hint">
        Reitin linjasta tulee reitin tunnistuslinja: pelaaja suorittaa reitin kulkemalla sen (90 % linjasta, 30 m:n sisällä,
        kumpaan suuntaan vain). Pelkät reittipisteet eivät kelpaa, vaan tiedostossa pitää olla jälki tai reitti.
      </p>
      <label class="btn btn-block file-btn">
        {file ? `Vaihda tiedosto (${file.name})` : "Valitse GPX-tiedosto"}
        <input type="file" class="sr-only" accept={ACCEPT} onChange={(e) => choose(e.currentTarget.files[0])} />
      </label>
      {busy && !preview && <p class="s" role="status">Luetaan…</p>}

      {preview && (
        <>
          <svg class="route-sketch" viewBox={`0 0 ${SKETCH_W} ${SKETCH_H}`} role="img" aria-label={`Reitin linja, ${String(preview.length_km).replace(".", ",")} kilometriä`}>
            <path d={sketchPath(preview.geometry, SKETCH_W, SKETCH_H)} fill="none" stroke="var(--rasti)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <p class="s">
            Linjan pituus {String(preview.length_km).replace(".", ",")} km, {preview.points} pistettä
            ({preview.parts} {preview.parts === 1 ? "osa" : "osaa"} tiedostossa).
          </p>
          {preview.geometry_parts > 1 && <p class="hint">Kaikki osat säilyvät erillisinä. Osien väliin ei lisätä yhdysviivaa. Valitse GPX:ään vain halutun reittivariantin osat.</p>}
          {warning && <div class="notice warn" role="alert">{warning}</div>}

          <div class="seg" role="group" aria-label="Tallennustapa">
            <button aria-pressed={mode === "new"} onClick={() => setMode("new")}>Uusi reitti</button>
            <button aria-pressed={mode === "replace"} onClick={() => setMode("replace")}>Korvaa linja</button>
          </div>

          {mode === "replace" ? (
            <div class="field">
              <label for="ri-replace">Reitti, jonka linja korvataan</label>
              <select id="ri-replace" value={form.replaceId} onChange={(e) => set({ replaceId: e.currentTarget.value })}>
                <option value="">Valitse reitti…</option>
                {candidates.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <p class="s">Vain linja vaihtuu kaikille pelaajille. Nimi, pisteet ja jo suoritetut reitit pysyvät ennallaan.</p>
            </div>
          ) : (
            <>
              <div class="field">
                <label for="ri-name">Nimi</label>
                <input id="ri-name" maxLength={120} autocomplete="off" {...text("name")} />
              </div>
              <div class="field-row">
                <div class="field">
                  <label for="ri-mun">Kunta</label>
                  <input id="ri-mun" list="ri-mun-list" maxLength={80} autocomplete="off" {...text("municipality")} />
                  <datalist id="ri-mun-list">{towns.map((t) => <option key={t} value={t} />)}</datalist>
                </div>
                <div class="field">
                  <label for="ri-area">Alue (valinnainen)</label>
                  <input id="ri-area" maxLength={80} autocomplete="off" placeholder="Oletus: kunta" {...text("area")} />
                </div>
              </div>
              <div class="field">
                <label for="ri-diff">Vaativuus</label>
                <select id="ri-diff" value={form.difficulty} onChange={(e) => set({ difficulty: e.currentTarget.value })}>
                  {DIFFICULTY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{difficultyLabel(o, preview)}</option>)}
                </select>
              </div>
              <div class="field-row">
                <div class="field">
                  <label for="ri-pts">Pisteet (oma arvo)</label>
                  <input id="ri-pts" inputMode="numeric" placeholder={shownPoints == null ? "" : `Laskettu ${shownPoints}`} {...text("points")} />
                </div>
                <div class="field">
                  <label for="ri-km">Pituus (km)</label>
                  <input id="ri-km" inputMode="decimal" placeholder={String(preview.length_km).replace(".", ",")} {...text("length")} />
                </div>
              </div>
              <p class="s">
                Reitti antaa {shownPoints ?? "?"} pistettä. Tyhjä pistekenttä käyttää kaavaa (300 + 50 × km) × vaativuuskerroin.
                Pituus-kenttään voit antaa virallisen pituuden, jos se eroaa linjan pituudesta.
              </p>
              <div class="field">
                <label for="ri-desc">Kuvaus (valinnainen)</label>
                <textarea id="ri-desc" maxLength={600} {...text("description")} />
              </div>
              <div class="field">
                <label for="ri-note">Huomautus (valinnainen)</label>
                <input id="ri-note" maxLength={300} autocomplete="off" {...text("note")} />
              </div>
            </>
          )}
        </>
      )}

      {errors.length > 0 && (
        <div class="notice warn" role="alert">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}

      <div class="sheet-actions">
        {preview && (
          <button class="btn btn-rasti btn-block" disabled={busy} onClick={save}>
            {busy ? "Tallennetaan…" : mode === "replace" ? "Korvaa reitin linja" : "Lisää reitti"}
          </button>
        )}
        <button class="btn btn-block" onClick={onClose}>Peruuta</button>
      </div>
    </Sheet>
  );
}

